import React, { useEffect, useRef, useState } from 'react';
import { LocalizationProvider, DateRange as MUIDateRange } from '@mui/x-date-pickers-pro';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { getLocaleFromDateFns } from '@sekoia/shared/helpers/date-fns/getLocalFromDateFns';
import { Button, Stack } from '@mui/material';
import { DateRange, ErrorType } from './DateRange';

type BasicDateRangePickerProps = {
  onChange: (date: DateRange) => void;
  defaultValue?: DateRange;
  disableFuture?: boolean;
  allowedNumberOfMonths?: number;
  showClearButton?: boolean;
  smallSize?: boolean;
  acceptEmptyDates?: boolean;
  marginTop?: number | string;
};

const minWidthToMakeTheEmptyPlaceholderFitWithClearButton = 364;
const minWidthToMakeTheEmptyPlaceholderFitWithoutClearButton = 300;

const BasicDateRangePicker: React.FC<React.PropsWithChildren<BasicDateRangePickerProps>> = (props) => {
  const {
    onChange,
    defaultValue,
    disableFuture,
    allowedNumberOfMonths,
    showClearButton = false,
    smallSize,
    acceptEmptyDates = false,
    marginTop = '8px',
  } = props;
  const { t } = useTranslation(['global']);

  const [value, setValue] = useState<DateRange>(
    new DateRange(defaultValue?.start ?? null, defaultValue?.end ?? null, allowedNumberOfMonths, acceptEmptyDates),
  );
  const [errorType, setErrorType] = useState<ErrorType | undefined>(undefined);

  // This is only to reset from a parent component,
  // date should only be set to begin with, and then only changen internally in this component.
  useEffect(() => {
    if (defaultValue && defaultValue.start === null && defaultValue.end === null) {
      setValue(new DateRange(defaultValue.start, defaultValue.end, allowedNumberOfMonths, acceptEmptyDates));
    }
  }, [acceptEmptyDates, allowedNumberOfMonths, defaultValue]);

  const format = moment.localeData().longDateFormat('L').replace(/Y/g, 'y').replace(/D/g, 'd');

  const prevValue = useRef<DateRange | undefined>();

  const isSameRange = (a?: DateRange, b?: DateRange) => {
    return a?.start?.getTime?.() === b?.start?.getTime?.() && a?.end?.getTime?.() === b?.end?.getTime?.();
  };

  useEffect(() => {
    if (!value || isSameRange(prevValue.current, value)) {
      return;
    } else {
      setErrorType(value.errorType);
      onChange(value);
      prevValue.current = value;
    }
  }, [acceptEmptyDates, allowedNumberOfMonths, onChange, value]);

  const handleDateRangeUpdated = (selectedDateRange: MUIDateRange<Date>) => {
    setValue(new DateRange(selectedDateRange[0], selectedDateRange[1], allowedNumberOfMonths, acceptEmptyDates));
  };

  const handleClearDateRange = () => {
    setValue(new DateRange(null, null, allowedNumberOfMonths, acceptEmptyDates));
  };

  return (
    <Stack
      direction="row"
      alignContent={'center'}
      sx={{
        marginTop: marginTop,
        maxWidth: smallSize
          ? showClearButton
            ? minWidthToMakeTheEmptyPlaceholderFitWithClearButton
            : minWidthToMakeTheEmptyPlaceholderFitWithoutClearButton
          : undefined,
      }}
    >
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={getLocaleFromDateFns()}
        localeText={{ start: t('global:inputs.date.picker.startDate'), end: t('global:inputs.date.picker.endDate') }}
      >
        <DateRangePicker
          sx={{ width: '100%' }}
          value={[value.start, value.end]}
          format={format}
          onChange={handleDateRangeUpdated}
          disableFuture={disableFuture}
          displayWeekNumber
          slotProps={{
            textField: {
              margin: 'dense',
              error: errorType !== undefined,
              helperText:
                errorType !== undefined
                  ? errorType === 'range'
                    ? t('global:inputs.date.maxInterval')
                    : t('global:inputs.date.invalidDate')
                  : '',
              size: smallSize ? 'small' : 'medium',
            },
            fieldSeparator: { children: t('inputs.date.picker.to') },
          }}
        />
      </LocalizationProvider>
      {showClearButton && (
        <Button
          data-testid={'BasicDateRangePickerClearButton'}
          variant="text"
          component="span"
          onClick={handleClearDateRange}
          title={t('global:inputs.date.clear')}
        >
          {t('global:inputs.date.clear')}
        </Button>
      )}
    </Stack>
  );
};

export default React.memo(BasicDateRangePicker);
